import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { MessageSquareQuote, Plus } from 'lucide-react';

import Loader from 'components/Loader/Loader';
import { loadTestimonialRequests } from 'redux/modules/testimonials';
import { calendarDaysAgoLabel } from 'helpers/postedAgo';
import fieldStyles from 'components/Portfolio/EntryDialog/EntryDialog.module.scss';
import styles from './ManageTestimonials.module.scss';

// Longer quotes are clamped with a "Show more".
const LONG_QUOTE = 280;

const byline = (item) =>
  [item.name, item.company_name !== item.name && item.company_name]
    .filter(Boolean)
    .join(' · ');

const Quote = ({ text }) => {
  const [open, setOpen] = useState(false);
  const long = text.length > LONG_QUOTE;

  return (
    <>
      <p className={`${styles.quote} ${long && !open ? styles.clamped : ''}`}>
        “{text}”
      </p>
      {long && (
        <button
          type="button"
          className={styles.linkBtn}
          onClick={() => setOpen(!open)}
          aria-expanded={open}
        >
          {open ? 'Show less' : 'Show more'}
        </button>
      )}
    </>
  );
};

Quote.propTypes = {
  text: PropTypes.string.isRequired,
};

const TestimonialCard = ({ testimonial }) => (
  <li className={styles.card}>
    <span className={styles.byline}>{byline(testimonial)}</span>
    <span className={styles.meta}>
      {[
        testimonial.project_name,
        testimonial.created_at &&
          moment(testimonial.created_at).format('MMM YYYY'),
      ]
        .filter(Boolean)
        .join(' · ')}
    </span>
    {testimonial.text && <Quote text={testimonial.text} />}
  </li>
);

TestimonialCard.propTypes = {
  testimonial: PropTypes.object.isRequired,
};

const RequestCard = ({ request }) => {
  const sent = calendarDaysAgoLabel(request.created_at);
  return (
    <li className={`${styles.card} ${styles.requestCard}`}>
      <div className={styles.requestText}>
        <span className={styles.byline}>{byline(request)}</span>
        <span className={styles.meta}>
          {[request.email, request.project_name].filter(Boolean).join(' · ')}
        </span>
      </div>
      <span className={styles.status}>
        {sent ? `Sent ${sent}` : 'Sent'} · No reply yet
      </span>
    </li>
  );
};

RequestCard.propTypes = {
  request: PropTypes.object.isRequired,
};

/**
 * Testimonials on a portfolio. The owner also sees the requests they've
 * sent that nobody has written yet, and can ask for another.
 */
const ManageTestimonials = ({
  user,
  isOwn,
  testimonials,
  total,
  loading,
  more,
  onLoadMore,
  onAsk,
  onClose,
}) => {
  const dispatch = useDispatch();
  const [tab, setTab] = useState('portfolio');
  const [requests, setRequests] = useState(null);
  const [requestsFailed, setRequestsFailed] = useState(false);
  // Bumped by "Try again" to load the requests again.
  const [requestsAttempt, setRequestsAttempt] = useState(0);

  useEffect(() => {
    if (!isOwn) {
      return undefined;
    }
    let live = true;
    setRequests(null);
    setRequestsFailed(false);
    dispatch(loadTestimonialRequests(user.username)).then(
      (result) => live && setRequests(result?.testimonials || []),
      () => live && setRequestsFailed(true)
    );
    return () => {
      live = false;
    };
  }, [dispatch, isOwn, user.username, requestsAttempt]);

  const loadingRequests = isOwn && requests === null && !requestsFailed;
  const requestCount = requests ? requests.length : 0;
  const isEmpty =
    !loading &&
    !loadingRequests &&
    !requestsFailed &&
    total === 0 &&
    requestCount === 0;
  const showTabs = isOwn && !isEmpty;

  const askButton = (
    <button type="button" className={fieldStyles.primaryBtn} onClick={onAsk}>
      <Plus size={16} strokeWidth={2.4} aria-hidden="true" />
      Ask for a testimonial
    </button>
  );

  const renderPortfolioTab = () => {
    if (testimonials.length === 0 && !loading) {
      return (
        <p className={styles.tabEmpty}>
          {isOwn
            ? 'Nobody has written one yet. Your requests are under Requests sent.'
            : `${user.displayname} has no testimonials yet.`}
        </p>
      );
    }
    return (
      <>
        <ul className={styles.list}>
          {testimonials.map((testimonial) => (
            <TestimonialCard key={testimonial.id} testimonial={testimonial} />
          ))}
        </ul>
        <Loader loading={loading} />
        {more && !loading && (
          <button
            type="button"
            className={`${fieldStyles.secondaryBtn} ${styles.more}`}
            onClick={onLoadMore}
          >
            Show more
          </button>
        )}
      </>
    );
  };

  const renderRequestsTab = () => {
    if (requestsFailed) {
      return (
        <div className={styles.tabEmpty} role="alert">
          <p>We couldn’t load your requests.</p>
          <button
            type="button"
            className={fieldStyles.secondaryBtn}
            onClick={() => setRequestsAttempt(requestsAttempt + 1)}
          >
            Try again
          </button>
        </div>
      );
    }
    if (loadingRequests) {
      return <Loader loading />;
    }
    return requestCount === 0 ? (
      <p className={styles.tabEmpty}>No requests waiting for a reply.</p>
    ) : (
      <ul className={styles.list}>
        {requests.map((request) => (
          <RequestCard key={request.id} request={request} />
        ))}
      </ul>
    );
  };

  const renderBody = () => {
    if (isEmpty && isOwn) {
      return (
        <div className={styles.empty}>
          <span className={styles.emptyIcon} aria-hidden="true">
            <MessageSquareQuote size={24} strokeWidth={2} />
          </span>
          <h3 className={styles.emptyTitle}>No testimonials yet</h3>
          <p className={styles.emptyText}>
            Ask a client you’ve worked with to write a few lines about the
            project. It goes on your portfolio as soon as they do.
          </p>
          {askButton}
        </div>
      );
    }
    if (isEmpty) {
      return (
        <p className={styles.tabEmpty}>
          {user.displayname} has no testimonials yet.
        </p>
      );
    }
    if (loading && testimonials.length === 0) {
      return <Loader loading />;
    }
    return tab === 'requests' ? renderRequestsTab() : renderPortfolioTab();
  };

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <div className={styles.headerRow}>
          <div className={styles.headerText}>
            <h2 className={styles.title}>
              Testimonials
              {!isOwn && total > 0 && (
                <span className={styles.count}> · {total}</span>
              )}
            </h2>
            {isOwn && (
              <p className={styles.subtitle}>
                A testimonial shows on your portfolio as soon as the client
                writes it.
              </p>
            )}
          </div>
          {showTabs && askButton}
        </div>
        {showTabs && (
          <div className={styles.tabs} role="tablist">
            {[
              { id: 'portfolio', label: 'On your portfolio', count: total },
              { id: 'requests', label: 'Requests sent', count: requestCount },
            ].map(({ id, label, count }) => (
              <button
                key={id}
                type="button"
                role="tab"
                id={`testimonials-tab-${id}`}
                aria-selected={tab === id}
                aria-controls="testimonials-panel"
                className={`${styles.tab} ${
                  tab === id ? styles.tabActive : ''
                }`}
                onClick={() => setTab(id)}
              >
                {label}
                <span className={styles.tabCount}>{count}</span>
              </button>
            ))}
          </div>
        )}
      </header>
      <div
        className={styles.body}
        id="testimonials-panel"
        role={showTabs ? 'tabpanel' : undefined}
        aria-labelledby={showTabs ? `testimonials-tab-${tab}` : undefined}
      >
        {renderBody()}
      </div>
      <footer className={styles.footer}>
        <button
          type="button"
          className={fieldStyles.secondaryBtn}
          onClick={onClose}
        >
          Done
        </button>
      </footer>
    </div>
  );
};

ManageTestimonials.propTypes = {
  user: PropTypes.object.isRequired,
  isOwn: PropTypes.bool,
  testimonials: PropTypes.array.isRequired,
  total: PropTypes.number,
  loading: PropTypes.bool,
  more: PropTypes.bool,
  onLoadMore: PropTypes.func.isRequired,
  onAsk: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ManageTestimonials;
